import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../api/client';

// Server-searched client picker. A company can hold thousands of customers, so this never loads them all:
// it asks /orders/clients for the first few matches of what was typed (name, code or mobile) and, when a
// client is already chosen, fetches just that one. Keyboard-first (CLAUDE.md §10): opens on focus, the first
// match is pre-highlighted, arrows/typing work without a click.
const PAGE = 30;

export interface PickedClient { id: string; name: string; clientCode?: string; phone?: string; gstNumber?: string; type?: string; [k: string]: any }

const unwrap = (r: any): PickedClient[] => (Array.isArray(r) ? r : r?.clients ?? r?.data ?? []);
const labelOf = (c: PickedClient) => `${c.clientCode ? `${c.clientCode} — ` : ''}${c.name}`;

export function ClientPicker({
  value, onChange, label = 'Client', required, error, helperText, disabled, size, autoFocus, type, sx,
}: {
  value: string;
  onChange: (id: string, client: PickedClient | null) => void;
  label?: string; required?: boolean; error?: boolean; helperText?: string; disabled?: boolean;
  size?: 'small' | 'medium'; autoFocus?: boolean;
  /** Restrict to one client type (e.g. 'MANUFACTURER'). */
  type?: string;
  sx?: object;
}) {
  const [input, setInput] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTerm(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  const { data: found, isFetching } = useQuery({
    queryKey: ['client-picker', type ?? '', term],
    queryFn: () => apiGet<any>('/orders/clients', { search: term || undefined, type, limit: PAGE }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
  const { data: chosen } = useQuery({
    queryKey: ['client-one', value],
    queryFn: () => apiGet<any>('/orders/clients', { id: value, limit: 1 }),
    enabled: !!value,
    staleTime: 60_000,
  });
  const selected = useMemo(() => (value ? unwrap(chosen)[0] ?? null : null), [value, chosen]);
  const options = useMemo(() => {
    const list = unwrap(found);
    return selected && !list.some((c) => c.id === selected.id) ? [selected, ...list] : list;
  }, [found, selected]);

  return (
    <Autocomplete
      fullWidth openOnFocus autoHighlight autoSelect={false} disabled={disabled} size={size} sx={sx}
      options={options}
      value={selected}
      // Server already filtered; don't re-filter the labels client-side (the mobile number isn't in the label).
      filterOptions={(o) => o}
      getOptionLabel={labelOf}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onInputChange={(_, v, reason) => { if (reason !== 'reset') setInput(v); else setInput(''); }}
      onChange={(_, v) => onChange(v?.id ?? '', v ?? null)}
      renderOption={(props, c) => (
        <li {...props} key={c.id}>{labelOf(c)}{c.phone ? ` · ${c.phone}` : ''}</li>
      )}
      renderInput={(params) => (
        <TextField
          {...params} label={`${label}${required ? ' *' : ''}`} error={error} helperText={helperText} autoFocus={autoFocus}
          InputProps={{
            ...params.InputProps,
            endAdornment: (<>{isFetching ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
          }}
        />
      )}
    />
  );
}
